/**
 * familyGroups.ts - pure family-unit builder for the Manage Users page.
 *
 * Groups user accounts into family units from the students collection: two
 * accounts belong to the same family when they appear together in any
 * student's familyUids[], and families merge transitively (a parent listed
 * on two siblings unions both sides into one unit). A staff account whose
 * uid sits in a student's familyUids joins the unit like any other adult;
 * role is a rendering concern, not a grouping concern.
 *
 * IMPORTANT: this file imports NOTHING. `npm run test:handlers` drives it
 * under node --experimental-strip-types (same pattern as lib/photoUpload.ts),
 * where Firebase imports would not resolve.
 *
 * Family names are DERIVED, never stored - the Firestore rules deploy
 * pipeline is frozen, so a new field is impossible. Identity (and the color
 * band) keys off the sorted member uids, NOT the display name, so renaming
 * a dancer or parent never changes the family's color.
 */

/** Loose student shape: StudentDoc satisfies this, malformed data cannot break it. */
export interface FamilyGroupStudentInput {
  id: string;
  firstName?: unknown;
  lastName?: unknown;
  onCompTeam?: unknown;
  familyUids?: unknown;
  /** StudentDoc's own jrStaffDesignated mirror (normalizeStudentDoc in
   *  students.ts). Carried through so the Manage Users family band's "is
   *  there a Jr Staff in this family" fact is known at the same moment the
   *  roster itself is, rather than depending only on the separate
   *  fetchStudentJrStaffDesignations() call, which can still be loading or
   *  can fail (R58-class review, Packet J2 follow-up). */
  jrStaffDesignated?: unknown;
}

export interface FamilyUnitStudent {
  id: string;
  firstName: string;
  lastName: string;
  onCompTeam: boolean;
  /** True when the student doc itself says Jr Staff, independent of the
   *  separate designation-status fetch. See FamilyGroupStudentInput above. */
  jrStaffDesignated: boolean;
}

export interface FamilyUnit {
  /**
   * Stable identity: the unit's sorted member uids joined with '|'.
   * Feed this to the kid-color derivation (djb2 hash into the palette) so
   * the card color survives renames and reloads.
   */
  key: string;
  /** Adult account uids, in first-appearance order across the roster. */
  uids: string[];
  /** Dancers in this unit, in roster order. */
  students: FamilyUnitStudent[];
  /** Derived display name, e.g. "Ford family" or "Ford & Baker family". */
  name: string;
}

/**
 * Defensive read of a familyUids value: only non-empty trimmed strings,
 * de-duplicated, original order. Anything malformed yields [] instead of
 * throwing, because one bad student doc must never blank the whole page.
 */
export function normalizeFamilyUids(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const v of value) {
    if (typeof v !== 'string') continue;
    const t = v.trim();
    if (!t || seen.has(t)) continue;
    // Skip external-intake sentinels (zapier:studio-pro, public:<uuid>).
    // registrations.ts documents these live in familyUids as a "consumer-side
    // rule" (isExternalRegistrationUid = contains ':'), and #388 only guards
    // NEW placements - a pre-#388 doc can still carry one. Left in, a sentinel
    // shared across students would union unrelated households onto one card
    // and count as a phantom "login with no account record."
    if (t.includes(':')) continue;
    seen.add(t);
    out.push(t);
  }
  return out;
}

function cleanName(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function lastToken(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean);
  return parts.length > 0 ? parts[parts.length - 1] : '';
}

/**
 * Derive the family display name.
 *
 *   1. Most common dancer last name in the unit + " family".
 *   2. Exact two-way tie for most common: both names, ampersand-joined in
 *      first-appearance order ("Ford & Baker family"). Ampersand, never a
 *      slash - house style.
 *   3. Three-plus-way tie, or no dancer has a last name: the first parent's
 *      last name (last token of their display name), in unit member order.
 *   4. Nothing usable at all: "Family".
 */
export function deriveFamilyName(
  students: ReadonlyArray<FamilyUnitStudent>,
  parentNamesInOrder: ReadonlyArray<string>
): string {
  // Case-insensitive counting, first-seen casing wins, first-appearance
  // order preserved (Map iteration order) so ties are deterministic.
  const counts = new Map<string, { display: string; count: number }>();
  for (const s of students) {
    const last = s.lastName.trim();
    if (!last) continue;
    const k = last.toLowerCase();
    const cur = counts.get(k);
    if (cur) cur.count += 1;
    else counts.set(k, { display: last, count: 1 });
  }
  if (counts.size > 0) {
    let max = 0;
    for (const { count } of counts.values()) {
      if (count > max) max = count;
    }
    const tied = [...counts.values()].filter((c) => c.count === max);
    if (tied.length === 1) return `${tied[0].display} family`;
    if (tied.length === 2) return `${tied[0].display} & ${tied[1].display} family`;
    // 3+ way tie falls through to the parent-surname rule.
  }
  for (const parentName of parentNamesInOrder) {
    const last = lastToken(cleanName(parentName));
    if (last) return `${last} family`;
  }
  return 'Family';
}

/**
 * Build family units from the roster via union-find over (student,
 * familyUids). Pure function of its inputs:
 *
 *   - students: the full students list (roster order matters only for
 *     deterministic tie-breaks and chip order).
 *   - userNames: uid -> display name, used ONLY for the parent-surname
 *     fallback in name derivation. A uid missing here still groups fine.
 *
 * A user account that appears in no student's familyUids lands in no unit.
 * A student with no valid familyUids forms no unit (there is no account to
 * show a card for; the dancer still lives on the Dancers page).
 */
export function buildFamilyUnits(
  students: ReadonlyArray<FamilyGroupStudentInput>,
  userNames: Readonly<Record<string, string | null | undefined>>
): FamilyUnit[] {
  // --- union-find over uids ------------------------------------------------
  const parent = new Map<string, string>();
  const add = (x: string) => {
    if (!parent.has(x)) parent.set(x, x);
  };
  const find = (x: string): string => {
    let root = x;
    while (parent.get(root) !== root) root = parent.get(root) as string;
    // Path compression keeps repeat lookups cheap on big rosters.
    let cur = x;
    while (cur !== root) {
      const next = parent.get(cur) as string;
      parent.set(cur, root);
      cur = next;
    }
    return root;
  };
  const union = (a: string, b: string) => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent.set(rb, ra);
  };

  const uidOrder: string[] = []; // global first-appearance order
  const seenUids = new Set<string>();
  const linked: { student: FamilyUnitStudent; uids: string[] }[] = [];

  for (const s of students) {
    if (!s || typeof s.id !== 'string' || s.id === '') continue;
    const uids = normalizeFamilyUids(s.familyUids);
    if (uids.length === 0) continue;
    linked.push({
      student: {
        id: s.id,
        firstName: cleanName(s.firstName),
        lastName: cleanName(s.lastName),
        onCompTeam: s.onCompTeam === true,
        jrStaffDesignated: s.jrStaffDesignated === true,
      },
      uids,
    });
    for (const uid of uids) {
      add(uid);
      if (!seenUids.has(uid)) {
        seenUids.add(uid);
        uidOrder.push(uid);
      }
    }
    for (let i = 1; i < uids.length; i++) union(uids[0], uids[i]);
  }

  // --- collect members + dancers per root ----------------------------------
  const unitsByRoot = new Map<string, { uids: string[]; students: FamilyUnitStudent[] }>();
  for (const uid of uidOrder) {
    const root = find(uid);
    let unit = unitsByRoot.get(root);
    if (!unit) {
      unit = { uids: [], students: [] };
      unitsByRoot.set(root, unit);
    }
    unit.uids.push(uid);
  }
  for (const { student, uids } of linked) {
    const unit = unitsByRoot.get(find(uids[0]));
    if (unit) unit.students.push(student);
  }

  // --- derive name + stable key --------------------------------------------
  const units: FamilyUnit[] = [];
  for (const { uids, students: kids } of unitsByRoot.values()) {
    const key = [...uids].sort().join('|');
    const parentNames = uids.map((uid) =>
      Object.prototype.hasOwnProperty.call(userNames, uid)
        ? cleanName(userNames[uid])
        : ''
    );
    units.push({ key, uids, students: kids, name: deriveFamilyName(kids, parentNames) });
  }
  units.sort((a, b) => a.name.localeCompare(b.name) || a.key.localeCompare(b.key));
  return units;
}
